import { z } from 'zod';
import { label } from './text-policy.js';
import { BADGE_MEDIA, BADGE_MEDIA_IDS, BADGE_MEDIA_KINDS, BADGE_TEMPLATE_SOURCES } from './badge-template.js';
/**
 * The four Brother models S35 supports (PRD-009 §supported models, docs/plans/badge-printing.md
 * §"Model differences"). All four speak raw TCP on port 9100.
 *
 * ORDER IS LOAD-BEARING: `BadgePrinterModelCode` in packages/db/prisma/schema.prisma mirrors this
 * tuple INDEX FOR INDEX (S35-TASK-025), because a Postgres enum's integer position is baked into
 * every persisted row. Append only; never reorder.
 */
export const BADGE_PRINTER_MODELS = ['QL_820NWB', 'QL_820NWBC', 'QL_720NW', 'QL_710W'];
/** The per-model capability table ADR-0023's amendment keeps in TypeScript ONLY. */
export const BADGE_PRINTER_MODEL_CAPABILITIES = {
    QL_820NWB: { id: 'QL_820NWB', supportsTwoColor: true, supportsEthernet: true },
    QL_820NWBC: { id: 'QL_820NWBC', supportsTwoColor: true, supportsEthernet: true },
    QL_720NW: { id: 'QL_720NW', supportsTwoColor: false, supportsEthernet: true },
    QL_710W: { id: 'QL_710W', supportsTwoColor: false, supportsEthernet: false },
};
/** Every supported model listens for raw jobs here. */
export const BADGE_PRINTER_DEFAULT_PORT = 9100;
export const BADGE_PRINTER_NAME_MAX_LENGTH = 80;
export const BADGE_PRINTER_HOST_MAX_LENGTH = 255;
/**
 * The rule BDG-03c could not make: a template knows no printer model, so "this stock needs two
 * colours and this model cannot print them" was unanswerable until the printer record existed.
 *
 * DERIVED FROM `colorPlanes`, NEVER FROM A HARDCODED LIST OF MEDIA IDS. A fourth medium must not
 * require this function to be edited — it must only be added to `BADGE_MEDIA` with the right
 * `colorPlanes` and this rule keeps holding.
 */
export function badgePrinterSupportsMedia(model, media) {
    if (BADGE_MEDIA[media].colorPlanes > 1) {
        return BADGE_PRINTER_MODEL_CAPABILITIES[model].supportsTwoColor;
    }
    return true;
}
/**
 * What a printer can tell us about itself (PRT-11). MIRRORED INDEX FOR INDEX by
 * `BadgePrinterStatusCode` in schema.prisma (S35-TASK-025) — append only, never reorder.
 *
 * Stamped onto `BadgePrinterDTO.lastStatus` and `BadgePrintRecordDTO.printerStatus` by the
 * device print-record sync (`POST /api/device/v1/visitor/badge-print-records`), from what the
 * kiosk's pre-print `ESC i S` check actually observed.
 *
 * `UNAVAILABLE` is unlike every value above it: it is not something the printer said about
 * itself, it is the record that the printer said NOTHING — the status request timed out, the
 * connection closed, or the reply did not parse as a status block. A real QL-820NWB returns
 * zero bytes to `ESC i S` on port 9100, so on that hardware the check has never once run; some
 * printers are effectively write-only, and that is a property of the printer worth recording,
 * not a per-job error. Such a printer still prints — a visitor must not be stranded by a
 * write-only printer — but "verification was unavailable" becomes a visible fact instead of
 * a PRINTED row that reads identically to a verified one.
 */
export const BADGE_PRINTER_STATUS_CODES = [
    'READY',
    'NO_MEDIA',
    'END_OF_MEDIA',
    'CUTTER_JAM',
    'COVER_OPEN',
    'FEED_ERROR',
    'BUFFER_FULL',
    'SYSTEM_ERROR',
    'UNAVAILABLE',
];
/**
 * A hostname, an IPv4 literal or a BARE IPv6 literal (no brackets — the brackets are URL syntax
 * and this is not a URL). The character class is the point: it admits letters, digits, dot, dash,
 * underscore and colon and NOTHING ELSE, so a value that reaches a socket call or a log line can
 * carry no shell metacharacter, no whitespace and no path separator.
 */
export const badgePrinterHostSchema = z
    .string()
    .trim()
    .min(1)
    .max(BADGE_PRINTER_HOST_MAX_LENGTH)
    .regex(/^[A-Za-z0-9._:-]+$/);
export const badgePrinterPortSchema = z.number().int().min(1).max(65535);
/** `POST /api/web/visitor/badge-printers` request body (PRT-01). */
export const registerBadgePrinterSchema = z
    .object({
    locationId: z.string().min(1),
    name: label({ max: BADGE_PRINTER_NAME_MAX_LENGTH }),
    model: z.enum(BADGE_PRINTER_MODELS),
    host: badgePrinterHostSchema,
    port: badgePrinterPortSchema.default(BADGE_PRINTER_DEFAULT_PORT),
    media: z.enum(BADGE_MEDIA_IDS),
})
    .strict()
    .superRefine((value, ctx) => {
    if (!badgePrinterSupportsMedia(value.model, value.media)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['media'],
            message: 'BADGE_PRINTER_MEDIA_UNSUPPORTED',
        });
    }
});
/**
 * `PATCH /api/web/visitor/badge-printers/:printerId` request body.
 *
 * NO `locationId`: a printer never moves location. Moving one is deleting it here and registering
 * it there, which is also what physically happened.
 *
 * NO `superRefine`, DELIBERATELY. A partial body cannot see the merged `(model, media)` pair — a
 * request that changes only `media` has no `model` to check it against, and a schema that guessed
 * would either reject a valid edit or wave through an invalid one. THE SERVICE RE-APPLIES
 * `badgePrinterSupportsMedia` TO THE MERGED RECORD; S35-TASK-027 owns that check and raises
 * `BADGE_PRINTER_MEDIA_UNSUPPORTED` from there.
 */
export const updateBadgePrinterSchema = z
    .object({
    name: label({ max: BADGE_PRINTER_NAME_MAX_LENGTH }).optional(),
    model: z.enum(BADGE_PRINTER_MODELS).optional(),
    host: badgePrinterHostSchema.optional(),
    port: badgePrinterPortSchema.optional(),
    media: z.enum(BADGE_MEDIA_IDS).optional(),
    /**
     * PROMOTION ONLY, AND `true` IS THE ONLY VALUE. PRT-01c says exactly one
     * printer per location carries the default flag, so "stop being the default"
     * is not an expressible action — it would leave the location with none. You
     * promote a different printer instead, which is why this is
     * `z.literal(true)` and not `z.boolean()`: `false` is not a no-op to be
     * ignored quietly, it is a request that has no meaning, and `.strict()`
     * plus a literal answers it with a 400 instead of silently doing nothing.
     *
     * The FIELD lives here; the BEHAVIOUR does not. S35-TASK-028's PATCH route
     * calls the field update and then, iff this is `true`, the promotion; the
     * clear-then-set transaction that moves the flag belongs to S35-TASK-026's
     * `setDefaultBadgePrinter`. `changed` is computed over `name`/`model`/
     * `host`/`port`/`media` only — a promotion is its own audited fact
     * (`.default_changed`), not a field edit.
     */
    isDefault: z.literal(true).optional(),
})
    .strict();
/** `GET /api/web/visitor/badge-printers` query. */
export const listBadgePrintersQuerySchema = z
    .object({
    locationId: z.string().min(1).optional(),
})
    .strict();
/**
 * What the ADMIN CONSOLE receives.
 *
 * `host` AND `port` DO CROSS THE WIRE, and that is a decision, not an oversight — see
 * "Edge cases and failure modes" in S35-TASK-023 for the S7 argument.
 *
 * DELIBERATELY ABSENT (S7, the same allow-list rule `badgeTemplateDTOSchema` records): `tenantId`
 * (the token already decided it) and `defaultForLocationId` (an internal discriminator column —
 * `isDefault` is the boolean a client actually needs).
 */
export const badgePrinterDTOSchema = z.object({
    id: z.string().min(1),
    locationId: z.string().min(1),
    name: z.string().max(BADGE_PRINTER_NAME_MAX_LENGTH),
    model: z.enum(BADGE_PRINTER_MODELS),
    host: z.string().max(BADGE_PRINTER_HOST_MAX_LENGTH),
    port: z.number().int(),
    media: z.enum(BADGE_MEDIA_IDS),
    /** PRT-01c. Derived server-side from `defaultForLocationId !== null`, never client-supplied. */
    isDefault: z.boolean(),
    /** Resolved from `BADGE_PRINTER_MODEL_CAPABILITIES`, so a console never holds the table. */
    supportsTwoColor: z.boolean(),
    /** Stamped by the device print-record sync from the kiosk's most recent pre-print `ESC i S`
     *  check against this printer (PRT-11). `null` until a kiosk has printed here at least once. */
    lastStatus: z.enum(BADGE_PRINTER_STATUS_CODES).nullable(),
    lastStatusAt: z.string().datetime().nullable(),
    /**
     * DERIVED AT READ TIME, NEVER STORED, and never client-supplied. The media of the badge
     * template that would actually resolve for a visit at this printer's location right now —
     * walking the BDG-01/BDG-08 ladder LOCATION_VISITOR_TYPE → VISITOR_TYPE → LOCATION → TENANT
     * → built-in — compared against this printer's own `media` column. `null` is the ordinary
     * case: they agree.
     *
     * When they disagree, `templateMedia` is what the template would render on and
     * `templateSource` names which rung of the ladder that disagreeing template sits on, so the
     * console can point the operator straight at the editor that needs a matching update.
     * `BUILT_IN` is a real rung here, unlike `BadgeTemplateScope`'s four: it means the tenant
     * has saved no template at this location's scope at all, so the effective template is the
     * built-in default (`BUILT_IN_BADGE_TEMPLATE`) and ITS media is what the admin preview and
     * the reference layout actually use — a tenant that has configured nothing is not a tenant
     * with nothing to disagree about.
     *
     * A NON-BLOCKING ADVISORY, DELIBERATELY, not a rejection and not an error code. The two
     * columns are independently editable rows: `VisitorBadgeTemplate.media` is a design-time
     * choice (the admin preview, `badgeLayoutFits()`, the golden fixtures) and
     * `VisitorBadgePrinter.media` is what is physically loaded right now, and a roll swap is
     * inherently a two-step change — so a disagreement is transient and legitimate
     * mid-migration, not a defect. Rejecting it at template save is impossible in general: a
     * tenant-default template has `locationId = null` and serves every location, so it could
     * never be saved by a tenant whose locations run mixed media. Rejecting it at printer edit
     * would stop the printer record from stating the truth about the roll actually loaded,
     * which it exists to do. Contrast `BADGE_PRINTER_MEDIA_UNSUPPORTED`: that is an intrinsic
     * incompatibility between one pair of values that can never be made to work by waiting, so
     * it IS a rejection.
     *
     * A NAMED, TYPED FIELD, NOT A GENERIC `warnings[]` ARRAY. There is no warnings-array
     * precedent anywhere in this codebase, and a named field is what every other derived DTO
     * field here already looks like (`supportsTwoColor`, `isDefault`).
     */
    templateMediaMismatch: z
        .object({
        templateMedia: z.enum(BADGE_MEDIA_IDS),
        templateSource: z.enum(BADGE_TEMPLATE_SOURCES),
    })
        .nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * What the KIOSK receives — RESOLVED flags and RESOLVED geometry, no model enum.
 *
 * This shape IS the ADR-0023 amendment in type form: Dart holds behaviour keyed on flags, never a
 * model enum and never a second copy of the media table. `supportsTwoColor`,
 * `printableDotsWidth`, `printableDotsHeight` and `colorPlanes` are all resolved on the server —
 * the first from `BADGE_PRINTER_MODEL_CAPABILITIES`, the last three from `BADGE_MEDIA[media]` —
 * so adding a fifth model or a fourth medium stays a server change. `mediaKind`, `mediaWidthMm`,
 * `mediaLengthMm` and `leftMarginPins` (S35-TASK-055) are the physical geometry the Brother raster
 * encoder needs — `ESC i z`'s media-type byte and n3/n4 fields, and the left-margin pin shift
 * inside the fixed 90-byte raster line — all resolved from `BADGE_MEDIA[media]` too.
 */
export const resolvedBadgePrinterDTOSchema = z.object({
    printerId: z.string().min(1),
    name: z.string().max(BADGE_PRINTER_NAME_MAX_LENGTH),
    host: z.string().max(BADGE_PRINTER_HOST_MAX_LENGTH),
    port: z.number().int(),
    media: z.enum(BADGE_MEDIA_IDS),
    supportsTwoColor: z.boolean(),
    printableDotsWidth: z.number().int(),
    printableDotsHeight: z.number().int(),
    colorPlanes: z.number().int(),
    /** `'CONTINUOUS'` | `'DIE_CUT'`. The encoder's `ESC i z` media-type byte and its `ESC i d`
     *  margin both turn on this, and the printer's own status block reports it back for the PRT-11
     *  comparison. RESOLVED from `BADGE_MEDIA[media].kind` so Dart never holds the table. */
    mediaKind: z.enum(BADGE_MEDIA_KINDS),
    /** Millimetres. `ESC i z` n3, and the value at status-block offset 10 is compared against it. */
    mediaWidthMm: z.number().int(),
    /** Millimetres. `ESC i z` n4 for die-cut, and the value at status-block offset 17. */
    mediaLengthMm: z.number().int(),
    /** Unprintable head pins left of the printable area — the shift the encoder applies inside the
     *  fixed-width raster line. See `BadgeMedia.leftMarginPins`. */
    leftMarginPins: z.number().int(),
});
//# sourceMappingURL=badge-printer.js.map